import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { OrgVariablesModel } from '@models/org-variables.model';
import { TranslatePipe } from '@ngx-translate/core';
import { UiKitModule } from '../../../../../../shared/ui-kit/ui-kit.module';
import { MatOption, MatSelect } from '@angular/material/select';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ValidatorsHelpers } from '../../../../../../helpers/validators.helpers';
import { CustomDirectionality } from '../../../../../../services/directionality.service';
import { IntegerOnlyDirective } from '../../directives/integer-only.directive';
import { FloatOnlyDirective } from '../../directives/float-only.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EnableEscCloseDirective } from '../../../../../../shared/directives/enable-esc-close.directive';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../store/app.state';
import * as UserSettingsSelectors from '@states/user-settings/user-settings.selectors';
import { OrgVariablesSelectors } from '@states/org-variables/org-variables.selector-type';
import { selectAllVariableNames } from '@states/org-variables/org-variables.selectors';
import { debounceTime, map, Observable, take } from 'rxjs';
import { NgxControlError } from 'ngxtension/control-error';
import { UiButtonComponent } from '@ui';

@Component({
  selector: 'app-org-variable-dialog',
  imports: [
    MatDialogClose,
    TranslatePipe,
    UiKitModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    FormsModule,
    MonacoEditorModule,
    IntegerOnlyDirective,
    FloatOnlyDirective,
    NgxControlError,
    UiButtonComponent,
  ],
  hostDirectives: [EnableEscCloseDirective],
  templateUrl: './org-variable-dialog.component.html',
  styleUrl: './org-variable-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgVariableDialogComponent implements OnInit {
  protected form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required],
      asyncValidators: [this.validateNameExists.bind(this)],
    }),
    type: new FormControl<OrgVariablesModel.VariableType | null>('string', [Validators.required]),
    value: new FormControl<OrgVariablesModel.VariableValueTypes | null>(null, [Validators.required]),
  });

  protected editorOptions = signal({ language: 'json', automaticLayout: true });

  protected variable: OrgVariablesModel.Variable | null = null;

  protected direction;

  private selectSelectedUserTheme$ = this.store$.pipe(select(UserSettingsSelectors.selectUserTheme));
  private existingNames$ = this.store$.select(OrgVariablesSelectors.selectAllVariableNames);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrgVariablesModel.Variable,
    private dialogRef: MatDialogRef<OrgVariableDialogComponent>,
    private directionality: CustomDirectionality,
    private destroyRef: DestroyRef,
    private store$: Store<AppState>,
  ) {}

  ngOnInit(): void {
    const dataVariable = this.data;
    if (dataVariable) {
      this.form.controls.name.disable();
      this.variable = dataVariable;
      if (dataVariable.type === 'json' && typeof dataVariable.value !== 'number') {
        const formattedValue = JSON.stringify(dataVariable.value, null, 2);
        this.form.patchValue(
          {
            ...dataVariable,
            value: formattedValue,
          },
          { emitEvent: false },
        );
        this.form.controls.value.addValidators([ValidatorsHelpers.jsonValidator]);
      } else {
        this.form.patchValue(this.data, { emitEvent: false });
      }
    }
    this.direction = this.directionality.value;

    this.selectSelectedUserTheme$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userTheme) => {
      const theme = userTheme === 'dark' ? 'vs-dark' : 'vs';
      this.editorOptions.update((current) => ({ ...current, theme }));
    });

    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.form.controls.value.setValue(null, { emitEvent: false });
      if (type === 'json') {
        this.form.controls.value.addValidators([ValidatorsHelpers.jsonValidator]);
      } else {
        this.form.controls.value.removeValidators([ValidatorsHelpers.jsonValidator]);
      }
    });
  }

  public onAdd(): void {
    const formValue = {
      ...this.form.getRawValue(),
      value:
        this.form.value.type === 'json' ? JSON.parse(this.form.value.value as string) : this.form.value.value,
    };
    this.dialogRef.close(formValue);
  }

  public onInit(editor) {
    setTimeout(() => {
      editor.getAction('editor.action.formatDocument').run();
    }, 500);
  }

  private validateNameExists(control: AbstractControl): Observable<ValidationErrors | null> {
    const name = control.value;
    return this.existingNames$.pipe(
      debounceTime(300),
      take(1),
      map((existingNames) => {
        return existingNames.map((value) => value.toString().toLowerCase()).includes(name.toLowerCase())
          ? { nameExists: true }
          : null;
      }),
    );
  }
}
